import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./Checkout.css";

import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";

const Checkout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, userSettings, loading } = useAuth();

  const [planKey, setPlanKey] = useState("");
  const [billing, setBilling] = useState("monthly");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [statusMsg, setStatusMsg] = useState("");

  // NEW: display currency (UI only)
  const [displayCurrency, setDisplayCurrency] = useState("USD");
  const [fxRate, setFxRate] = useState(null); // USD -> displayCurrency
  const [fxFetchedAt, setFxFetchedAt] = useState("");

  // NEW: Paystack charge currency (always ZAR) + rate USD->ZAR
  const [fxRateZar, setFxRateZar] = useState(null); // USD -> ZAR
  const [fxZarFetchedAt, setFxZarFetchedAt] = useState("");

  // Env: your deployed function URLs
  const PAYSTACK_INIT_URL = process.env.REACT_APP_PAYSTACK_INIT_URL; // createPaystackCheckout
  const FX_RATE_URL = process.env.REACT_APP_FX_RATE_URL; // getFxRate

  // Plans in USD (anchor currency)
  const PLAN_CATALOG = useMemo(
    () => ({
      free: {
        name: "Free (PAYG)",
        monthly: 0,
        annual: 0,
        tagline: "Start free, pay only when you exceed quotas.",
      },
      startup: { name: "Startup", monthly: 5, annual: 5 * 10, tagline: "Best for a single business website." },
      business: { name: "Business", monthly: 20, annual: 20 * 10, tagline: "Best value for serious businesses." },
      agency: { name: "Agency", monthly: 50, annual: 50 * 10, tagline: "For developers & agencies." },
    }),
    []
  );

  useEffect(() => {
    const fromUrl = (searchParams.get("plan") || "").toLowerCase().trim();
    const fromStorage = (localStorage.getItem("kairos_pending_plan") || "").toLowerCase().trim();
    const resolved = fromUrl || fromStorage;
    if (resolved) setPlanKey(resolved);
  }, [searchParams]);

  const selectedPlan = PLAN_CATALOG[planKey];

  const amountUsd = useMemo(() => {
    if (!selectedPlan) return 0;
    return billing === "annual" ? selectedPlan.annual : selectedPlan.monthly;
  }, [selectedPlan, billing]);

  // Guess currency from locale (best-effort). If it fails, keep USD.
  useEffect(() => {
    try {
      const locale = navigator.language || "en-US";
      if (locale.includes("ZA")) setDisplayCurrency("ZAR");
      else if (locale.includes("GB")) setDisplayCurrency("GBP");
      else if (
        locale.startsWith("de") ||
        locale.startsWith("fr") ||
        locale.startsWith("es") ||
        locale.startsWith("it")
      )
        setDisplayCurrency("EUR");
      else setDisplayCurrency("USD");
    } catch {
      setDisplayCurrency("USD");
    }
  }, []);

  // Fetch live FX USD -> displayCurrency (for UI only)
  useEffect(() => {
    const run = async () => {
      if (!FX_RATE_URL) return;
      if (!displayCurrency) return;

      try {
        setFxRate(null);
        setFxFetchedAt("");

        if (displayCurrency === "USD") {
          setFxRate(1);
          setFxFetchedAt(new Date().toISOString());
          return;
        }

        const url = `${FX_RATE_URL}?from=USD&to=${encodeURIComponent(displayCurrency)}`;
        const resp = await fetch(url);
        const data = await resp.json();
        if (!resp.ok) throw new Error(data?.error || "FX fetch failed");

        setFxRate(Number(data.rate));
        setFxFetchedAt(data.fetchedAt || "");
      } catch {
        // If FX fails, fall back to USD-only display
        setFxRate(null);
        setFxFetchedAt("");
      }
    };

    run();
  }, [FX_RATE_URL, displayCurrency]);

  // NEW: Fetch live FX USD -> ZAR (for Paystack charge amount)
  useEffect(() => {
    const run = async () => {
      if (!FX_RATE_URL) return;

      try {
        setFxRateZar(null);
        setFxZarFetchedAt("");

        const url = `${FX_RATE_URL}?from=USD&to=ZAR`;
        const resp = await fetch(url);
        const data = await resp.json();
        if (!resp.ok) throw new Error(data?.error || "FX fetch failed");

        setFxRateZar(Number(data.rate));
        setFxZarFetchedAt(data.fetchedAt || "");
      } catch {
        setFxRateZar(null);
        setFxZarFetchedAt("");
      }
    };

    run();
  }, [FX_RATE_URL]);

  const amountLocal = useMemo(() => {
    if (displayCurrency === "USD") return amountUsd;
    if (!fxRate) return null;
    return Number((amountUsd * fxRate).toFixed(2));
  }, [amountUsd, fxRate, displayCurrency]);

  // NEW: amount to CHARGE Paystack (always ZAR)
  const amountZar = useMemo(() => {
    if (!fxRateZar) return null;
    return Number((amountUsd * fxRateZar).toFixed(2));
  }, [amountUsd, fxRateZar]);

  // Must be logged in
  useEffect(() => {
    if (loading) return;
    if (!currentUser) navigate("/authentication", { replace: true });
  }, [currentUser, loading, navigate]);

  // Invalid plan
  useEffect(() => {
    if (!planKey) return;
    if (!PLAN_CATALOG[planKey]) setError("Invalid plan selected. Please choose a plan again.");
    else setError("");
  }, [planKey, PLAN_CATALOG]);

  const settingsRef = useMemo(() => {
    if (!currentUser) return null;
    return doc(db, "users", currentUser.uid, "settings", "main");
  }, [currentUser]);

  const beginCheckout = async () => {
    if (!currentUser) return;

    if (!PAYSTACK_INIT_URL) {
      setError("Missing REACT_APP_PAYSTACK_INIT_URL in your frontend environment.");
      return;
    }

    if (!planKey || !selectedPlan) {
      setError("Please select a valid plan.");
      return;
    }

    setBusy(true);
    setError("");
    setStatusMsg("Preparing checkout...");

    try {
      // Save intent first
      if (settingsRef) {
        await updateDoc(settingsRef, {
          planKey,
          plan: planKey === "free" ? "free" : "payment-ongoing",
          planStatus: planKey === "free" ? "active" : "payment-ongoing",
          billingCycle: billing,
          displayCurrency,
          updatedAt: serverTimestamp(),
        });
      }

      // Free plan
      if (planKey === "free" || amountUsd === 0) {
        localStorage.removeItem("kairos_pending_plan");
        setStatusMsg("Free plan activated. Redirecting...");
        navigate("/workspace", { replace: true });
        return;
      }

      // ✅ Ensure we have a USD->ZAR rate before charging
      if (amountZar == null) {
        setError("Unable to load USD→ZAR exchange rate. Please try again in a moment.");
        setStatusMsg("");
        return;
      }

      setStatusMsg("Opening Paystack secure checkout (Test Mode)...");

      // ✅ IMPORTANT:
      // - UI can show GBP/EUR/etc (approx only)
      // - Paystack must be charged in ZAR
      // - We send amountZar to backend
      const resp = await fetch(PAYSTACK_INIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uid: currentUser.uid,
          email: currentUser.email || "",
          planKey,
          billing,

          // Anchor currency
          amountUsd,

          // Charge currency for Paystack (ZAR)
          amountZar,
          chargeCurrency: "ZAR",

          // optional: store what the user chose for display
          displayCurrency,
        }),
      });

      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || "Could not create Paystack checkout.");
      if (!data?.authorization_url) throw new Error("Paystack did not return an authorization_url.");

      localStorage.removeItem("kairos_pending_plan");

      // store reference before redirecting away (optional)
      if (data?.reference) {
        localStorage.setItem("kairos_last_paystack_reference", data.reference);
      }

      window.location.href = data.authorization_url;
    } catch (err) {
      setError(err?.message || "Checkout failed. Please try again.");
      setStatusMsg("");
    } finally {
      setBusy(false);
    }
  };

  const goBackToPricing = () => navigate("/pricing");

  const formatMoney = (amount, currency) => {
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
    } catch {
      return `${currency} ${amount}`;
    }
  };

  return (
    <main className="home">
      <section className="checkout">
        <div className="checkout-header">
          <h2>
            Secure <span className="gradient-text">Checkout</span>
          </h2>
          <p className="checkout-subtitle">
            Confirm your plan and billing cycle. You’ll be redirected to Paystack Test Mode to complete payment.
          </p>
        </div>

        <div className="checkout-grid">
          <div className="checkout-card">
            <h3>Selected Plan</h3>

            <div className="plan-pill-row">
              {Object.keys(PLAN_CATALOG).map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`plan-pill ${planKey === k ? "active" : ""}`}
                  onClick={() => setPlanKey(k)}
                  disabled={busy}
                >
                  {PLAN_CATALOG[k].name}
                </button>
              ))}
            </div>

            {!selectedPlan ? (
              <div className="notice">
                <p>Please select a plan to continue.</p>
                <button type="button" className="secondary-btn" onClick={goBackToPricing}>
                  Back to pricing
                </button>
              </div>
            ) : (
              <>
                <div className="plan-summary">
                  <div className="plan-title">
                    <h4>{selectedPlan.name}</h4>
                    <span className="plan-tag">{billing === "annual" ? "Annual" : "Monthly"}</span>
                  </div>

                  <p className="plan-tagline">{selectedPlan.tagline}</p>

                  <div className="price-row">
                    <div className="price-amount">
                      {formatMoney(amountUsd, "USD")}
                      <span className="price-suffix">{billing === "annual" ? "/year" : "/month"}</span>
                    </div>
                    {billing === "annual" && amountUsd > 0 && <div className="save-badge">2 months free</div>}
                  </div>

                  {/* Currency display selector (UI only) */}
                  <div className="mini-details" style={{ marginTop: 12 }}>
                    <div className="mini-row">
                      <span>Display currency</span>
                      <span className="muted">
                        <select
                          className="currency-select"
                          value={displayCurrency}
                          onChange={(e) => setDisplayCurrency(e.target.value)}
                          disabled={busy}
                        >
                          <option value="USD">USD</option>
                          <option value="ZAR">ZAR</option>
                          <option value="EUR">EUR</option>
                          <option value="GBP">GBP</option>
                          <option value="AUD">AUD</option>
                          <option value="CAD">CAD</option>
                        </select>
                      </span>
                    </div>

                    <div className="mini-row">
                      <span>Approx in {displayCurrency}</span>
                      <span className="muted">
                        {amountLocal == null ? "Loading rate..." : formatMoney(amountLocal, displayCurrency)}
                      </span>
                    </div>

                    <div className="mini-row">
                      <span>FX (display) updated</span>
                      <span className="muted">{fxFetchedAt ? new Date(fxFetchedAt).toLocaleString() : "—"}</span>
                    </div>

                    <div className="mini-row">
                      <span>Charged via Paystack</span>
                      <span className="muted">
                        {amountZar == null ? "Loading USD→ZAR..." : `${formatMoney(amountZar, "ZAR")} (ZAR)`}
                      </span>
                    </div>

                    <div className="mini-row">
                      <span>FX (USD→ZAR) updated</span>
                      <span className="muted">{fxZarFetchedAt ? new Date(fxZarFetchedAt).toLocaleString() : "—"}</span>
                    </div>
                  </div>
                </div>

                <div className="billing-toggle-wrap">
                  <div className="billing-toggle" role="group" aria-label="Billing period toggle">
                    <button
                      className={billing === "monthly" ? "billing-btn active" : "billing-btn"}
                      onClick={() => setBilling("monthly")}
                      type="button"
                      disabled={busy}
                    >
                      Monthly
                    </button>
                    <button
                      className={billing === "annual" ? "billing-btn active" : "billing-btn"}
                      onClick={() => setBilling("annual")}
                      type="button"
                      disabled={busy}
                    >
                      Annual <span className="billing-badge">2 months free</span>
                    </button>
                  </div>
                  <p className="billing-subtext">
                    Annual billing is charged once per year and includes a discount equivalent to 2 months free.
                  </p>
                </div>

                <div className="mini-details">
                  <div className="mini-row">
                    <span>Account</span>
                    <span className="muted">{currentUser?.email || "—"}</span>
                  </div>
                  <div className="mini-row">
                    <span>Current status</span>
                    <span className="muted">{userSettings?.planStatus || "—"}</span>
                  </div>
                  <div className="mini-row">
                    <span>Selected plan key</span>
                    <span className="muted">{planKey}</span>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="checkout-card featured">
            <h3>Order Summary</h3>

            <div className="summary-box">
              <div className="summary-row">
                <span>Plan</span>
                <span>{selectedPlan?.name || "—"}</span>
              </div>
              <div className="summary-row">
                <span>Billing cycle</span>
                <span>{billing}</span>
              </div>
              <div className="summary-row">
                <span>Total (USD)</span>
                <span className="summary-total">{formatMoney(amountUsd, "USD")}</span>
              </div>
              <div className="summary-row">
                <span>Approx ({displayCurrency})</span>
                <span className="summary-total">{amountLocal == null ? "…" : formatMoney(amountLocal, displayCurrency)}</span>
              </div>
              <div className="summary-row">
                <span>Charged (ZAR)</span>
                <span className="summary-total">{amountZar == null ? "…" : formatMoney(amountZar, "ZAR")}</span>
              </div>
              <div className="summary-note">
                <p>
                  Your pricing is USD-based. Paystack will charge in ZAR using a live USD→ZAR rate at checkout time.
                </p>
              </div>
            </div>

            {error && <p className="error">{error}</p>}
            {statusMsg && <p className="success">{statusMsg}</p>}

            <button type="button" className="primary-btn" onClick={beginCheckout} disabled={busy || !selectedPlan || !!error}>
              {busy ? "Processing..." : amountUsd === 0 ? "Activate Free Plan" : "Proceed to Payment (Test)"}
            </button>

            <p className="tiny">
              By continuing, you agree to the Kairos policies and billing terms. You can cancel anytime (where applicable).
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Checkout;
